import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["sidebar", "phasesList", "singleDatePicker", "rangeDatePicker", "tabLabel", "inloopTypeLabel"]

  connect() {
    // Listen for form submission to capture date values
    const form = document.getElementById('create_inloop_form')
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this))
    }

    // Initialize phases array with default phases
    this.phases = [
      { title: "Discovery", description: "Understand the problem, users, and context by gathering insights, data, and requirements.", position: 0 },
      { title: "Design", description: "Generate ideas and create potential solutions through sketches, prototypes, and iterative concept development.", position: 1 },
      { title: "Decide", description: "Evaluate the options, select the most promising solution, and define a plan for moving forward.", position: 2 },
      { title: "Validate", description: "Test the chosen solution with users or stakeholders to confirm it works, meets needs, and solves the problem effectively.", position: 3 }
    ]

    this.nextPosition = 4
    this.phasesModified = true // Allow edit/delete from the start

    // Get icon templates
    this.iconTemplates = {
      edit: document.querySelector('#icon-templates [data-icon="edit"]')?.innerHTML || '',
      check: document.querySelector('#icon-templates [data-icon="check"]')?.innerHTML || '',
      trash: document.querySelector('#icon-templates [data-icon="trash"]')?.innerHTML || ''
    }

    // Render the default phases immediately
    this.renderPhases()
  }

  addPhase(event) {
    event.preventDefault()

    const newPhase = {
      title: `Phase ${this.nextPosition + 1}`,
      description: "Enter phase description...",
      position: this.nextPosition
    }

    this.phases.push(newPhase)
    this.nextPosition++
    this.phasesModified = true
    this.renderPhases()
  }

  editPhase(event) {
    event.preventDefault()
    const phaseIndex = parseInt(event.currentTarget.dataset.index)
    const phase = this.phases[phaseIndex]

    // Get the phase card element
    const phaseCard = event.currentTarget.closest('.bg-white')
    const titleEl = phaseCard.querySelector('p.font-semibold')
    const descEl = phaseCard.querySelector('p.text-xs')

    // Replace title with input
    const titleInput = document.createElement('input')
    titleInput.type = 'text'
    titleInput.value = phase.title
    titleInput.className = 'text-sm font-semibold text-[#030712] border border-[#d1d5db] rounded px-2 py-1 w-full'
    titleInput.dataset.field = 'title'
    titleEl.replaceWith(titleInput)

    // Replace description with textarea
    const descTextarea = document.createElement('textarea')
    descTextarea.value = phase.description
    descTextarea.className = 'text-xs text-[#4b5563] border border-[#d1d5db] rounded px-2 py-1 w-full resize-none'
    descTextarea.rows = 2
    descTextarea.dataset.field = 'description'
    descEl.replaceWith(descTextarea)

    // Change edit button to save button - use icon template
    const editBtn = event.currentTarget
    editBtn.innerHTML = this.iconTemplates.check
    editBtn.dataset.action = 'click->inloop-phases#savePhase'
    editBtn.dataset.index = phaseIndex
  }

  savePhase(event) {
    event.preventDefault()
    const phaseIndex = parseInt(event.currentTarget.dataset.index)

    // Get the phase card element
    const phaseCard = event.currentTarget.closest('.bg-white')
    const titleInput = phaseCard.querySelector('input[data-field="title"]')
    const descTextarea = phaseCard.querySelector('textarea[data-field="description"]')

    // Update phase data
    this.phases[phaseIndex].title = titleInput.value
    this.phases[phaseIndex].description = descTextarea.value

    // Mark as modified
    this.phasesModified = true

    // Re-render all phases
    this.renderPhases()
  }

  removePhase(event) {
    event.preventDefault()
    const phaseIndex = parseInt(event.currentTarget.dataset.index)

    // Remove phase from array
    this.phases.splice(phaseIndex, 1)

    // Reindex positions
    this.phases.forEach((phase, index) => {
      phase.position = index
    })

    this.nextPosition = this.phases.length
    this.phasesModified = true
    this.renderPhases()
  }

  renderPhases() {
    if (!this.hasPhasesListTarget) return

    const canEdit = this.phasesModified

    this.phasesListTarget.innerHTML = this.phases.map((phase, index) => {
      const editButtonClass = canEdit
        ? 'border border-[#d1d5db] rounded-md w-8 h-8 flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer'
        : 'border border-[#d1d5db] rounded-md w-8 h-8 flex items-center justify-center opacity-50 cursor-not-allowed'

      const deleteButtonClass = canEdit
        ? 'border border-[#d1d5db] rounded-md w-8 h-8 flex items-center justify-center hover:bg-red-50 hover:border-red-300 transition-colors cursor-pointer'
        : 'border border-[#d1d5db] rounded-md w-8 h-8 flex items-center justify-center opacity-50 cursor-not-allowed'

      const editAction = canEdit ? 'click->inloop-phases#editPhase' : ''
      const deleteAction = canEdit ? 'click->inloop-phases#removePhase' : ''

      return `
        <div class="bg-white border border-[#f2f3f5] rounded-lg px-3 py-2 flex gap-3 md:gap-5 items-start">
          <div class="bg-[#ebebff] rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0">
            <span class="text-sm font-semibold text-[#1f2937]">${index + 1}</span>
          </div>
          <div class="flex-1 flex flex-col gap-2">
            <p class="text-sm font-semibold text-[#030712]">${phase.title}</p>
            <p class="text-xs text-[#4b5563] line-clamp-2 hidden sm:block">${phase.description}</p>
          </div>
          <div class="flex gap-1 flex-shrink-0">
            <button type="button"
              class="${editButtonClass}"
              ${canEdit ? `data-action="${editAction}"` : ''}
              ${canEdit ? `data-index="${index}"` : ''}
              ${!canEdit ? 'disabled' : ''}>
              ${this.iconTemplates.edit}
            </button>
            <button type="button"
              class="${deleteButtonClass}"
              ${canEdit ? `data-action="${deleteAction}"` : ''}
              ${canEdit ? `data-index="${index}"` : ''}
              ${!canEdit ? 'disabled' : ''}>
              ${this.iconTemplates.trash}
            </button>
          </div>
        </div>
      `
    }).join('')
  }

  handleFormSubmit(event) {
    // Get the selected inloop type
    const inloopType = document.getElementById('inloop_type_hidden')?.value || 'single'

    // For single type: copy date from portal input to form
    if (inloopType === 'single') {
      // Try multiple selectors to find the input
      let startDateInput = this.singleDatePickerTarget.querySelector('input[name="start_date"]')
      if (!startDateInput) {
        startDateInput = document.querySelector('input[name="start_date"]')
      }
      if (!startDateInput) {
        // Try finding by data attribute if it exists
        startDateInput = document.querySelector('[data-date-picker-target="input"]')
      }

      if (startDateInput && startDateInput.value) {
        // Create hidden inputs inside the form with inloop[] prefix to match other form fields
        const formStartDate = document.createElement('input')
        formStartDate.type = 'hidden'
        formStartDate.name = 'inloop[start_date]'
        formStartDate.value = startDateInput.value
        event.target.appendChild(formStartDate)

        const formEndDate = document.createElement('input')
        formEndDate.type = 'hidden'
        formEndDate.name = 'inloop[end_date]'
        formEndDate.value = startDateInput.value
        event.target.appendChild(formEndDate)
      }
    } else {
      // For range mode, copy dates from portal inputs to form
      let startInput = this.rangeDatePickerTarget.querySelector('input[name="inloop[start]"]')
      let endInput = this.rangeDatePickerTarget.querySelector('input[name="inloop[end]"]')

      if (!startInput) {
        startInput = document.querySelector('input[name="inloop[start]"]')
      }
      if (!endInput) {
        endInput = document.querySelector('input[name="inloop[end]"]')
      }
      if (!startInput) {
        startInput = document.querySelector('[data-date-picker-target="startInput"]')
      }
      if (!endInput) {
        endInput = document.querySelector('[data-date-picker-target="endInput"]')
      }

      // Copy date values to form with correct naming (inloop[] prefix)
      if (startInput && startInput.value) {
        const formStartDate = document.createElement('input')
        formStartDate.type = 'hidden'
        formStartDate.name = 'inloop[start_date]'
        formStartDate.value = startInput.value
        event.target.appendChild(formStartDate)
      }

      if (endInput && endInput.value) {
        const formEndDate = document.createElement('input')
        formEndDate.type = 'hidden'
        formEndDate.name = 'inloop[end_date]'
        formEndDate.value = endInput.value
        event.target.appendChild(formEndDate)
      }

      // Add phase data for multiple type inloops
      this.phases.forEach((phase, index) => {
        const titleInput = document.createElement('input')
        titleInput.type = 'hidden'
        titleInput.name = `inloop[activity_phases_attributes][${index}][title]`
        titleInput.value = phase.title
        event.target.appendChild(titleInput)

        const descInput = document.createElement('input')
        descInput.type = 'hidden'
        descInput.name = `inloop[activity_phases_attributes][${index}][description]`
        descInput.value = phase.description
        event.target.appendChild(descInput)

        const positionInput = document.createElement('input')
        positionInput.type = 'hidden'
        positionInput.name = `inloop[activity_phases_attributes][${index}][position]`
        positionInput.value = phase.position
        event.target.appendChild(positionInput)

        const statusInput = document.createElement('input')
        statusInput.type = 'hidden'
        statusInput.name = `inloop[activity_phases_attributes][${index}][status]`
        statusInput.value = 'draft'
        event.target.appendChild(statusInput)
      })
    }
  }

  toggleSidebar(event) {
    const selectedValue = event.target.value

    const labelText = selectedValue === "multiple" ? "Phases & Activities" : "Activities"

    if (selectedValue === "multiple") {
      this.showSidebar()
      this.updateTabLabel(labelText)
      this.updateInloopTypeLabel(labelText)
    } else {
      this.hideSidebar()
      this.updateTabLabel(labelText)
      this.updateInloopTypeLabel(labelText)
    }

    // Update hidden input for form submission
    const hiddenInput = document.getElementById("inloop_type_hidden")
    if (hiddenInput) {
      hiddenInput.value = selectedValue
    }
  }

  showSidebar() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("hidden")
      this.sidebarTarget.classList.add("flex")
    } else {
      console.error("Sidebar target not found!")
    }

    // Show range date picker, hide single date picker
    this.showRangeDatePicker()
    // Update position to bottom_left
    this.updateDatePickerPosition("bottom_left", this.rangeDatePickerTarget)
  }

  hideSidebar() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("flex")
      this.sidebarTarget.classList.add("hidden")
    }

    // Show single date picker, hide range date picker
    this.showSingleDatePicker()
    // Reset position to bottom_center
    this.updateDatePickerPosition("bottom_center", this.singleDatePickerTarget)
  }

  showRangeDatePicker() {
    if (this.hasRangeDatePickerTarget) {
      this.rangeDatePickerTarget.classList.remove("hidden")
    }
    if (this.hasSingleDatePickerTarget) {
      this.singleDatePickerTarget.classList.add("hidden")
    }
  }

  showSingleDatePicker() {
    if (this.hasSingleDatePickerTarget) {
      this.singleDatePickerTarget.classList.remove("hidden")
    }
    if (this.hasRangeDatePickerTarget) {
      this.rangeDatePickerTarget.classList.add("hidden")
    }
  }

  updateDatePickerPosition(position, datePickerContainer) {
    if (datePickerContainer) {
      // Find dropdown menu within date picker
      const dropdownMenu = datePickerContainer.querySelector("[data-dropdown-target='menu']")

      if (dropdownMenu) {
        // Position class mappings
        const positions = {
          bottom_center: ["top-full", "left-1/2", "-translate-x-1/2", "mt-2"],
          bottom_left: ["top-full", "left-0", "mt-2"],
          bottom_right: ["top-full", "right-0", "mt-2"]
        }

        // Remove all position classes
        const allPositionClasses = [
          "top-full", "bottom-full", "left-0", "right-0", "left-1/2",
          "-translate-x-1/2", "mt-2", "mb-2"
        ]
        dropdownMenu.classList.remove(...allPositionClasses)

        // Add new position classes
        const newClasses = positions[position] || positions.bottom_right
        dropdownMenu.classList.add(...newClasses)

      }
    }
  }

  updateTabLabel(labelText) {
    if (this.hasTabLabelTarget) {
      this.tabLabelTarget.textContent = labelText
    }
  }

  updateInloopTypeLabel(labelText) {
    if (this.hasInloopTypeLabelTarget) {
      this.inloopTypeLabelTarget.textContent = labelText
    }
  }
}
